import type {
  PartnerDecennale,
  PartnerInfos,
  Project,
  ProjectDocument,
  ProjectFolder,
  ProjectSubFolder,
} from "types";
import type { Planning, PlanningModel } from "types/planning";

export const DECENNALES_FOLDER_NAME = "Décennales";

// Keep in sync with PlanningSetupModal's MODEL_WEEKS.
const PLANNING_MODEL_WEEKS: Record<PlanningModel, number> = {
  "11_mois": 48,
  "12_mois": 52,
  "12_mois_ordre": 52,
  "13_mois_avec_conges": 56,
  "14_mois_avec_conges": 60,
  "15_mois_avec_conges": 64,
};

/** Compute the planning's end ISO date from start_date + model. */
export function computePlanningEndIso(planning: Planning | null | undefined): string | undefined {
  if (!planning?.start_date) return undefined;
  const weeks = PLANNING_MODEL_WEEKS[planning.model];
  if (!weeks) return undefined;
  const start = new Date(planning.start_date);
  const end = new Date(start.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
  return end.toISOString().slice(0, 10);
}

function decennaleRange(d: PartnerDecennale): { start: string; end: string } | null {
  const start = d.start_date || (d.year ? `${d.year}-01-01` : "");
  const end = d.end_date || (d.year ? `${d.year}-12-31` : "");
  if (!start || !end) return null;
  return { start, end };
}

/**
 * Pick the best décennale for a partner.
 *
 * With `planningEndIso`: prefer one whose coverage extends to the planning
 * end (end >= planningEnd), choosing the earliest still-valid start so we
 * attach a décennale that already covers the full chantier.
 *
 * Without `planningEndIso`: fall back to the one whose coverage ends
 * furthest in the future — "latest valid" — so we attach something useful
 * right when the partner is added, before any planning exists.
 *
 * Returns null only when every entry lacks usable dates.
 */
export function pickBestDecennale(
  decennales: PartnerDecennale[],
  planningEndIso?: string,
): PartnerDecennale | null {
  const dated = decennales
    .map((d) => ({ d, range: decennaleRange(d) }))
    .filter((x): x is { d: PartnerDecennale; range: { start: string; end: string } } => !!x.range);
  if (dated.length === 0) return null;

  if (planningEndIso) {
    const covering = dated.filter((x) => x.range.end >= planningEndIso);
    if (covering.length > 0) {
      covering.sort((a, b) => a.range.start.localeCompare(b.range.start));
      return covering[0].d;
    }
  }

  const sorted = [...dated].sort((a, b) => b.range.end.localeCompare(a.range.end));
  return sorted[0].d;
}

/**
 * Return every décennale we want attached to the project for a partner.
 *
 * - Keeps only entries with usable dates (start + end present).
 * - Filters out ones whose coverage has fully expired (end < today).
 * - Sorted oldest-first so a multi-year planning can be read in order.
 *
 * The coverage check that drives warnings is separate — see
 * `decennalesCoverPlanning`.
 */
export function pickRelevantDecennales(
  decennales: PartnerDecennale[],
): PartnerDecennale[] {
  const today = new Date().toISOString().slice(0, 10);
  const dated = decennales
    .map((d) => ({ d, range: decennaleRange(d) }))
    .filter((x): x is { d: PartnerDecennale; range: { start: string; end: string } } => !!x.range)
    .filter((x) => x.range.end >= today);
  dated.sort((a, b) => a.range.start.localeCompare(b.range.start));
  return dated.map((x) => x.d);
}

/**
 * Does the union of the partner's décennales cover [today, planningEndIso]?
 * Gaps inside the period count as missing coverage. Used for warnings.
 */
export function decennalesCoverPlanning(
  decennales: PartnerDecennale[],
  planningEndIso: string,
): boolean {
  const today = new Date().toISOString().slice(0, 10);
  const ranges = decennales
    .map(decennaleRange)
    .filter((r): r is { start: string; end: string } => !!r)
    .filter((r) => r.end >= today)
    .sort((a, b) => a.start.localeCompare(b.start));
  if (ranges.length === 0) return false;

  let cursor = today;
  for (const r of ranges) {
    if (r.start > cursor) return false; // gap
    if (r.end > cursor) cursor = r.end;
    if (cursor >= planningEndIso) return true;
  }
  return cursor >= planningEndIso;
}

function buildDecennaleDoc(
  partner: PartnerInfos,
  decennale: PartnerDecennale,
): ProjectDocument {
  const range = decennaleRange(decennale);
  const label = range
    ? `Décennale ${range.start} ${range.end}`
    : "Décennale";
  const key = range?.start || "na";
  return {
    id: `doc_dec_${partner.id}_${key}`,
    name: `${label}.pdf`,
    file: {
      id: decennale.gcs_path || `file_dec_${partner.id}_${key}`,
      name: `${label}.pdf`,
      octets: 0,
      url: decennale.url || "",
      created_at: null as any,
      type: "pdf",
    },
  };
}

function partnerLabel(p: PartnerInfos, storePartners: PartnerInfos[]): string {
  const fresh = storePartners.find((sp) => sp.id === p.id);
  return (
    fresh?.company_name ||
    fresh?.display_name ||
    p.company_name ||
    p.display_name ||
    p.id
  );
}

/**
 * Rebuild the project's "Décennales" folder from its current partners.
 *
 * Behaviour:
 *  - One subfolder per partner (excluding the "maître d'ouvrage" placeholder).
 *  - Each subfolder contains a single PDF — the best décennale (see
 *    `pickBestDecennale`).
 *  - Subfolders for partners no longer on the project are dropped.
 *  - Returns a list of French warnings for partners lacking décennales or
 *    lacking coverage for `planningEndIso`.
 *
 * Re-running this is idempotent: subfolder identity is keyed on partner id,
 * so a later run with a different "best" décennale replaces the document.
 */
export function syncDecennalesFolder(
  project: Project,
  storePartners: PartnerInfos[],
  planningEndIso?: string,
): { folders: ProjectFolder[]; warnings: string[] } {
  const folders: ProjectFolder[] = project.folders ? [...project.folders] : [];
  const warnings: string[] = [];

  const projectPartners = (project.infos?.partners || []).filter(
    (p) => p.id !== "maitre_ouvrage",
  );

  const existing = folders.find((f) => f.name === DECENNALES_FOLDER_NAME);
  const decennalesFolder: ProjectFolder = existing
    ? { ...existing }
    : {
        id: `folder_decennales_${Date.now()}`,
        name: DECENNALES_FOLDER_NAME,
        documents: [],
        sub_folders: [],
      };

  const subFolders: ProjectSubFolder[] = decennalesFolder.sub_folders
    ? [...decennalesFolder.sub_folders]
    : [];

  const keepSubIds = new Set<string>();

  for (const partner of projectPartners) {
    const name = partnerLabel(partner, storePartners);
    const storePartner = storePartners.find((p) => p.id === partner.id);
    const decennales = storePartner?.decennales || partner.decennales || [];

    if (decennales.length === 0) {
      warnings.push(`${name} — aucune décennale enregistrée`);
      continue;
    }

    const relevant = pickRelevantDecennales(decennales);
    if (relevant.length === 0) {
      warnings.push(`${name} — décennales expirées ou sans dates valides`);
      continue;
    }

    if (planningEndIso && !decennalesCoverPlanning(decennales, planningEndIso)) {
      warnings.push(`${name} — les décennales ne couvrent pas toute la durée du chantier`);
    }

    const subId = `subfolder_dec_${partner.id}`;
    keepSubIds.add(subId);
    const docs = relevant.map((d) => buildDecennaleDoc(partner, d));
    const idx = subFolders.findIndex((sf) => sf.id === subId);
    if (idx >= 0) {
      subFolders[idx] = { ...subFolders[idx], name, documents: docs };
    } else {
      subFolders.push({ id: subId, name, documents: docs });
    }
  }

  // Drop subfolders whose partner is no longer on the project. Preserve any
  // pre-existing subfolder whose id is not one we manage (external ones).
  decennalesFolder.sub_folders = subFolders.filter(
    (sf) => !sf.id.startsWith("subfolder_dec_") || keepSubIds.has(sf.id),
  );

  const folderIdx = folders.findIndex((f) => f.name === DECENNALES_FOLDER_NAME);
  if (folderIdx >= 0) {
    folders[folderIdx] = decennalesFolder;
  } else {
    folders.push(decennalesFolder);
  }

  return { folders, warnings };
}
